import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  HeartIcon,
  LightBulbIcon,
  RocketLaunchIcon,
  UsersIcon,
  ArrowLeftIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  FireIcon
} from '@heroicons/react/24/outline';
import restaurantLogo from '../assets/restaurant-logo.png';

const About = () => {
  // Intersection Observer for scroll animations
  useEffect(() => {
    const observerCallback = (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('opacity-100', 'translate-y-0');
          entry.target.classList.remove('opacity-0', 'translate-y-10');
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, { threshold: 0.1 });
    document.querySelectorAll('.reveal-on-scroll').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const values = [
    {
      icon: HeartIcon,
      title: 'Customer Intent',
      description: 'We don’t just build software; we solve the daily friction between diners and the kitchen.'
    },
    {
      icon: FireIcon,
      title: 'High Velocity',
      description: 'The restaurant world moves fast. So do we. We ship improvements and updates daily.'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Absolute Reliability',
      description: 'When it’s a packed Friday night, our infrastructure remains invisible and robust.'
    },
    {
      icon: GlobeAltIcon,
      title: 'Global Standard',
      description: 'Bringing world-class technology to every local table, regardless of size or location.'
    }
  ];

  const stats = [
    { number: '500+', label: 'Restaurant Partners' },
    { number: '1.2M+', label: 'Orders Processed' },
    { number: '99.9%', label: 'Platform Uptime' },
    { number: '< 2min', label: 'Setup Time' }
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white font-sans selection:bg-[#F5A623] selection:text-black">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-[#0D0D0D]/90 py-4 px-6 border-b border-white/10 backdrop-blur-md">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={restaurantLogo} alt="Ordyrr Logo" className="h-8 md:h-10 w-auto object-contain drop-shadow-[0_0_8px_rgba(255,255,255,0.1)]" />
          </Link>
          <Link to="/" className="text-sm font-bold text-gray-400 hover:text-white transition-colors flex items-center gap-2">
            <ArrowLeftIcon className="w-4 h-4" />
            Back Home
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-48 md:pb-32 px-6">
        <div className="max-w-4xl mx-auto text-center reveal-on-scroll opacity-0 translate-y-10 transition-all duration-1000 ease-out">
          <div className="inline-block border border-[#F5A623]/30 bg-[#F5A623]/5 rounded-full px-4 py-1.5 mb-8 text-xs font-bold tracking-wider text-[#F5A623]">
            OUR MISSION
          </div>
          <h1 className="text-5xl md:text-7xl font-black tracking-tighter mb-8 leading-[1.05]">
            Giving every table a<br/><span className="text-white">digital heartbeat.</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto font-light leading-relaxed">
            Ordyrr was founded on a simple premise: technology should empower the restaurant staff, not replace the human touch. We build tools that handle the data, so you can handle the dining experience.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="pb-24 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, i) => (
            <div key={i} className="text-center reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700" style={{ transitionDelay: `${i * 100}ms` }}>
              <div className="text-4xl md:text-5xl font-black text-white mb-2">{stat.number}</div>
              <div className="text-sm font-bold tracking-widest uppercase text-gray-600">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 md:py-32 px-6 bg-[#111111] border-y border-white/5 relative overflow-hidden">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-gradient-to-t from-[#F5A623]/5 to-transparent pointer-events-none"></div>
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-20 items-center">
          <div className="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-1000">
            <h2 className="text-3xl md:text-5xl font-black mb-10 tracking-tighter">Born in the kitchen,<br/>built for the world.</h2>
            <div className="space-y-6 text-gray-400 font-light leading-relaxed text-lg">
              <p>
                The seed for Ordyrr was planted during a chaotic Friday night dinner. We saw servers overwhelmed by manual order entry and diners waiting far too long for simple bill requests.
              </p>
              <p>
                We realized the solution wasn't just another iPad on a stand. It was a bridge—an invisible layer of technology that would allow customers to take control of their journey while feeding data directly into the restaurant's operational engine.
              </p>
              <p>
                Today, Ordyrr powers hundreds of outlets, helping them grow revenue by an average of 35% through smarter upselling and faster table turns.
              </p>
            </div>
          </div>
          <div className="relative group reveal-on-scroll opacity-0 scale-95 transition-all duration-1000 delay-200">
            <div className="aspect-square bg-[#0D0D0D] border border-white/10 rounded-3xl overflow-hidden relative p-12 flex items-center justify-center">
              <RocketLaunchIcon className="w-32 h-32 text-[#F5A623] animate-pulse" />
              {/* Abstract decorations */}
              <div className="absolute top-10 left-10 w-4 h-4 border border-[#F5A623]/30 rounded-full"></div>
              <div className="absolute bottom-10 right-10 w-8 h-8 border border-white/10 rounded-full"></div>
              <div className="absolute top-1/2 right-10 w-2 h-2 bg-white/10 rounded-full"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Grid */}
      <section className="py-24 md:py-32 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-20 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <h2 className="text-4xl md:text-5xl font-black mb-4 tracking-tight">Our Core Values</h2>
          <p className="text-gray-500 font-light">The principles that guide every line of code we write.</p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((value, i) => (
            <div 
              key={i} 
              className="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-1000 ease-out p-8 bg-[#111111] border border-white/5 rounded-2xl hover:border-[#F5A623]/30 transition-colors"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <value.icon className="w-10 h-10 text-[#F5A623] mb-6" />
              <h3 className="text-xl font-bold mb-3">{value.title}</h3>
              <p className="text-gray-500 text-sm font-light leading-relaxed">{value.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6">
        <div className="max-w-4xl mx-auto text-center reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out">
          <h2 className="text-4xl md:text-5xl font-black mb-8">Ready to grow with us?</h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/auth" className="w-full sm:w-auto bg-[#F5A623] hover:bg-[#E0961E] text-black px-10 py-5 rounded-full text-lg font-bold transition-all transform hover:scale-105">
              Start Your Journey
            </Link>
            <Link to="/contact" className="w-full sm:w-auto text-white bg-white/5 border border-white/20 hover:bg-white/10 px-10 py-5 rounded-full text-lg font-bold transition-all">
              Contact Sales
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] pt-20 pb-10 px-6 border-t border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start md:items-center gap-10">
          <div>
            <div className="mb-4">
              <img src={restaurantLogo} alt="Ordyrr Logo" className="h-8 md:h-10 w-auto object-contain drop-shadow-[0_0_8px_rgba(255,255,255,0.1)]" />
            </div>
            <p className="text-sm text-gray-500 max-w-xs font-light">
              Premium QR ordering and management platform for modern restaurants.
            </p>
          </div>
          <div className="flex gap-8 md:gap-16">
            <Link to="/features" className="text-sm text-gray-400 hover:text-white transition-colors">Features</Link>
            <Link to="/pricing" className="text-sm text-gray-400 hover:text-white transition-colors">Pricing</Link>
            <Link to="/terms" className="text-sm text-gray-400 hover:text-white transition-colors">Terms</Link>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-16 pt-8 border-t border-white/10 text-center text-xs text-gray-600">
          <p>&copy; {new Date().getFullYear()} Ordyrr. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default About;
